import { useAuthentication } from '@/composables/useAuthentication';
import { JsonLayerResponse, isJsonLayerResponse } from '@/interfaces/jsonLayerResponse';

interface UpdateFavoritesResponse extends JsonLayerResponse {
  responses: any[];
}

function isUpdateFavoritesResponse(jsonLayerResponse: JsonLayerResponse): jsonLayerResponse is UpdateFavoritesResponse {
  return (
    'responses' in jsonLayerResponse &&
    Array.isArray(jsonLayerResponse.responses)
  );
}

export interface UpdateChartsRecidAndStyleUrlSearchParams {
  chartRecid: number;
  styleUrlSearchParams: URLSearchParams
}

export async function updateCharts(updateChartsRecidsAndStyleUrlSearchParams: UpdateChartsRecidAndStyleUrlSearchParams[]): Promise<JsonLayerResponse[]> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to update charts - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;
  const updateChartURLs = [];

  for (let i = 0; i < updateChartsRecidsAndStyleUrlSearchParams.length; i++) {
    const { chartRecid, styleUrlSearchParams } = updateChartsRecidsAndStyleUrlSearchParams[i];

    const urlSearchParams = new URLSearchParams();

    let style = styleUrlSearchParams.toString();

    if (!style) {
      throw new Error(`Unable to update charts - styleUrlSearchParams is empty for chart at index ${i}.`);
    }

    style = '&' + style;

    urlSearchParams.set('cmd', 'update-favorite');
    urlSearchParams.set('chartcode', chartCodeCopy);
    urlSearchParams.set('recid', chartRecid.toString());
    urlSearchParams.set('style', encodeURIComponent(style));

    updateChartURLs.push(`json/api?${urlSearchParams.toString()}`);
  }

  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'batch',
        requests: JSON.stringify(updateChartURLs)
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info(`Update charts jsonLayerResponse for chartCode: ${chartCodeCopy}.`, jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to update charts for chartCode: ${chartCodeCopy}.`, e);

    throw new Error(`Failed to update charts for chartCode: ${chartCodeCopy}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to update charts for chartCode: ${chartCodeCopy} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to update charts for chartCode: ${chartCodeCopy} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  if (!isUpdateFavoritesResponse(jsonLayerResponse)) {
    throw new Error(`Failed to update charts for chartCode: ${chartCodeCopy} - jsonLayerResponse.success is true but jsonLayerResponse does not match UpdateFavoritesResponse interface - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  const jsonLayerResponses = jsonLayerResponse.responses.filter(isJsonLayerResponse);
  const numInvalidJsonLayerResponses = jsonLayerResponse.responses.length - jsonLayerResponses.length;

  if (numInvalidJsonLayerResponses > 0) {
    window.sc3WorkbenchLogger.warn(`json-layer batch command returned ${numInvalidJsonLayerResponses} invalid responses (which did not match the JsonLayerResponse interface) while updating charts for chartCode: ${chartCodeCopy}.`);
  }

  return jsonLayerResponses;
}
